/**
 * Every word the panel says.
 *
 * Copy lives in `lib/i18n/*.ts`, one file per owner, and this module merges
 * them. Nothing in a component is a literal string: copy scattered through JSX
 * cannot be reviewed as copy, and the backlog requires the catalogue (ATA-23,
 * ATA-34, ATA-35, ATA-44, ATA-45).
 *
 * ## Naming — read this before adding a key
 *
 *     <prefix>.<camelCaseName>          'tenantMail.testSent'
 *     <family>.<API_VALUE>              'memberStatus.SUSPENDED', 'action.platform.tenant_created'
 *
 * - **A page writes only under its own prefix, in its own file.** The prefix is
 *   the page folder in camelCase: `src/app/tenant/mail/` owns
 *   `lib/i18n/tenant-mail.ts` and the prefix `tenantMail.`. The type on that
 *   file (`Messages<'tenantMail'>`) refuses any other key, so twelve authors
 *   adding copy at once never touch the same file and never collide on a key.
 * - **Shared words are not restated.** Save, cancel, retry, loading, empty,
 *   error → `common.*`. Statuses, roles, permissions, audit actions → their
 *   family in `vocabulary.ts`. Navigation and tab labels → `nav.*` / `tab.*`.
 *   Those three files are owned by lib; ask rather than duplicate.
 * - **Families are keyed by the API's own value**, verbatim, so a label is
 *   `t(\`role.${role}\`)` and the compiler checks the family is complete. The
 *   label functions in `lib/format.ts` do this; call those, not `t` directly.
 * - **Placeholders are `{name}`**, filled by `t(key, { name })`. A placeholder
 *   left unfilled renders visibly as `{name}` rather than as a hole.
 * - **Arabic copy rules**: the tenant is المنشأة (never المستأجر), the brand is
 *   أيد, the panel is لوحة المنصّة. Identifiers, emails, slugs and URLs are not
 *   copy — they are data and render `dir="ltr"`.
 *
 * ## Adding a page
 *
 * Create `lib/i18n/<page>.ts` exporting `{…} as const satisfies Messages<'<prefix>'>`,
 * then add it to `CATALOGUES` below (one line, the only shared edit).
 *
 * Arabic only, for now. A second locale is a second set of these files and a
 * switch in `t`; nothing outside `lib/i18n*` changes.
 */

import { audit } from './i18n/audit';
import { common } from './i18n/common';
import { dashboard } from './i18n/dashboard';
import { defaults } from './i18n/defaults';
import { libMessages } from './i18n/lib';
import { models } from './i18n/models';
import { nav } from './i18n/nav';
import { permissions } from './i18n/permissions';
import { signIn } from './i18n/sign-in';
import { tenantAudit } from './i18n/tenant-audit';
import { tenantContent } from './i18n/tenant-content';
import { tenantMail } from './i18n/tenant-mail';
import { tenantMembers } from './i18n/tenant-members';
import { tenantMicrosoft } from './i18n/tenant-microsoft';
import { tenantModels } from './i18n/tenant-models';
import { tenantNew } from './i18n/tenant-new';
import { tenantOverview } from './i18n/tenant-overview';
import { tenantRoles } from './i18n/tenant-roles';
import { tenants } from './i18n/tenants';
import { ui } from './i18n/ui';
import { vocabulary } from './i18n/vocabulary';
import { workspace } from './i18n/workspace';

/**
 * Every catalogue, by owner. Exported so the spec can prove no two of them
 * share a key — the prefixes make it impossible by construction, and the test
 * is what notices when somebody widens a prefix to make a key fit.
 */
export const CATALOGUES = {
  common,
  nav,
  vocabulary,
  libMessages,
  ui,
  signIn,
  dashboard,
  tenants,
  tenantNew,
  workspace,
  tenantOverview,
  tenantContent,
  tenantMembers,
  tenantRoles,
  tenantMail,
  tenantMicrosoft,
  tenantModels,
  tenantAudit,
  permissions,
  models,
  audit,
  defaults,
} as const;

type Catalogues = typeof CATALOGUES;
type UnionToIntersection<U> = (U extends unknown ? (value: U) => void : never) extends (value: infer I) => void
  ? I
  : never;
type Catalogue = UnionToIntersection<Catalogues[keyof Catalogues]>;

const ar: Record<string, string> = Object.assign({}, ...Object.values(CATALOGUES)) as Record<string, string>;

/** Every key that exists. `t('members.titel')` does not compile. */
export type MessageKey = keyof Catalogue & string;

const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * Resolves a key, substituting `{named}` placeholders.
 *
 * A missing placeholder is left in the output rather than replaced with an
 * empty string, so a caller that forgot `{name}` produces a visible `{name}`
 * instead of a sentence with a hole in it that reads as finished.
 */
export function t(key: MessageKey, values?: Record<string, string | number>): string {
  const message = ar[key] ?? key;

  if (values === undefined) {
    return message;
  }

  return message.replace(PLACEHOLDER, (whole, name: string) => {
    const value = values[name];

    return value === undefined ? whole : String(value);
  });
}

/** Whether a key exists — for open-ended values such as audit actions. */
export function hasMessage(key: string): key is MessageKey {
  return Object.hasOwn(ar, key);
}

/** The keys under a prefix, without their prefix. */
export function messageKeysUnder(prefix: string): string[] {
  return Object.keys(ar)
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));
}
